// src/pages/Volunteer.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    GlobalStyles,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import volunteerImg from '../assets/volunteer/volunteer.jpg';
import { apiFetch } from '../utils/api';

/* —————————————————————————————————————
   US States
   ————————————————————————————————————— */
const US_STATES = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

/* —————————————————————————————————————
   Styled section wrapper (with "Required" badge)
   ————————————————————————————————————— */
function Section({ title, subtitle, required = false, children }) {
    return (
        <Paper elevation={1} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2 }}>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {title}{' '}
                    {required && (
                        <Typography component="span" variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                            • Required
                        </Typography>
                    )}
                </Typography>
                {subtitle && (
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                )}
            </Stack>
            <Divider sx={{ mb: 3 }} />
            {children}
        </Paper>
    );
}

/* —————————————————————————————————————
   Helpers for safe inputs
   ————————————————————————————————————— */
const stripCtl = (s) => s.replace(/[\u0000-\u001F\u007F]/g, '');
const clamp = (s, n) => (s.length > n ? s.slice(0, n) : s);

const DAYS = [
    { key: 'mon', label: 'Mon' },
    { key: 'tue', label: 'Tue' },
    { key: 'wed', label: 'Wed' },
    { key: 'thu', label: 'Thu' },
    { key: 'fri', label: 'Fri' },
    { key: 'sat', label: 'Sat' },
    { key: 'sun', label: 'Sun' },
];

/* —————————————————————————————————————
   Main form (used inside a Dialog)
   Exposes validity/submitting state to parent
   ————————————————————————————————————— */
function VolunteerForm({ formId, onSuccess, onValidityChange, onSubmittingChange }) {
    const initial = useMemo(
        () => ({
            // Contact
            first_name: '',
            last_name: '',
            email_address: '',
            phone_nbr: '',
            phone_type: 'cell',
            address: '',
            city: '',
            state: 'AL',
            zip_code: '',
            occupation: '',
            hobbies: '',

            // Per‑day availability (morning/afternoon)
            mon_morning: false,
            mon_afternoon: false,
            tue_morning: false,
            tue_afternoon: false,
            wed_morning: false,
            wed_afternoon: false,
            thu_morning: false,
            thu_afternoon: false,
            fri_morning: false,
            fri_afternoon: false,
            sat_morning: false,
            sat_afternoon: false,
            sun_morning: false,
            sun_afternoon: false,

            // Interests
            open_roberts_house: false,
            open_depot_museum: false,
            host_school_tours: false,
            clerical_skills: false,
            clerical_skills_notes: '',
            grant_writing: false,

            maintenance_repairs: false,
            maintenance_carpentry: false,
            maintenance_plumbing: false,
            maintenance_electrical: false,
            maintenance_painting: false,
            maintenance_other: false,
            maintenance_other_notes: '',

            fundraising_projects: false,
            public_relations: false,
            pr_newspapers: false,
            pr_social_media: false,
            pr_website: false,

            research_historical_topics: false,
            holiday_decorating: false,
            correspondence: false,
            yard_maintenance: false,

            computer_skills: false,
            computer_skills_notes: '',
            host_museum_receptions: false,

            housekeeping: false,
            housekeeping_roberts_home: false,
            housekeeping_depot_museum: false,

            providing_speakers: false,
            safety_security: false,

            other_talents: false,
            other_talents_notes: '',
        }),
        []
    );

    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Scroll to the bottom error alert when it appears
    const errorAlertRef = useRef(null);
    useEffect(() => {
        if (error && errorAlertRef.current) {
            errorAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            errorAlertRef.current.focus();
        }
    }, [error]);

    // Notify parent about submitting state
    useEffect(() => {
        onSubmittingChange?.(submitting);
    }, [submitting, onSubmittingChange]);

    // Generic safe setters
    const setSanitized = (field, max) => (e) => {
        let v = e.target.value || '';
        v = clamp(stripCtl(v), max);
        setForm((f) => ({ ...f, [field]: v }));
    };
    const setPhone = (e) => {
        let v = e.target.value || '';
        v = v.replace(/[^\d+\-().\s]/g, ''); // allow digits + ( ) - . space
        v = clamp(v, 25);
        setForm((f) => ({ ...f, phone_nbr: v }));
    };
    const setZip = (e) => {
        let v = e.target.value || '';
        v = v.replace(/[^\d-]/g, '');
        v = clamp(v, 10);
        setForm((f) => ({ ...f, zip_code: v }));
    };

    const handleCheck = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.checked }));

    const handleToggleGroup = (field, children = []) => (e) => {
        const checked = e.target.checked;
        setForm((f) => {
            const next = { ...f, [field]: checked };
            if (!checked) children.forEach((c) => (next[c] = false));
            return next;
        });
    };

    // Day part toggles
    const handleDay = (dayKey, part) => (e) => {
        const checked = e.target.checked;
        setForm((f) => ({ ...f, [`${dayKey}_${part}`]: checked }));
    };

    // ——— Required section checks ———
    const contactOk =
        form.first_name.trim() &&
        form.last_name.trim() &&
        form.email_address.trim() &&
        form.phone_nbr.trim() &&
        form.address.trim() &&
        form.city.trim() &&
        form.state.trim();

    const availabilityOk = DAYS.some(
        (d) => form[`${d.key}_morning`] || form[`${d.key}_afternoon`]
    );

    const interestKeys = [
        'open_roberts_house',
        'open_depot_museum',
        'host_school_tours',
        'clerical_skills',
        'grant_writing',
        'maintenance_repairs',
        'maintenance_carpentry',
        'maintenance_plumbing',
        'maintenance_electrical',
        'maintenance_painting',
        'maintenance_other',
        'fundraising_projects',
        'public_relations',
        'pr_newspapers',
        'pr_social_media',
        'pr_website',
        'research_historical_topics',
        'holiday_decorating',
        'correspondence',
        'yard_maintenance',
        'computer_skills',
        'host_museum_receptions',
        'housekeeping',
        'housekeeping_roberts_home',
        'housekeeping_depot_museum',
        'providing_speakers',
        'safety_security',
        'other_talents',
    ];
    const interestsOk = interestKeys.some((k) => !!form[k]);

    const formOk = Boolean(contactOk && availabilityOk && interestsOk);

    // Expose validity to parent to control footer Submit
    useEffect(() => {
        onValidityChange?.(formOk);
    }, [form, formOk, onValidityChange]);

    // Build the payload with aggregated day/time booleans
    const buildPayload = () => {
        const dayAvailable = (d) => form[`${d}_morning`] || form[`${d}_afternoon`];
        const anyMorning = DAYS.some((d) => form[`${d.key}_morning`]);
        const anyAfternoon = DAYS.some((d) => form[`${d.key}_afternoon`]);

        return {
            // Contact
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            email_address: form.email_address.trim(),
            phone_nbr: form.phone_nbr.trim(),
            phone_type: form.phone_type,
            address: form.address.trim(),
            city: form.city.trim(),
            state: form.state,
            zip_code: form.zip_code.trim(),
            occupation: form.occupation.trim(),
            hobbies: form.hobbies.trim(),

            // Required by DB (existing columns)
            available_mon: dayAvailable('mon'),
            available_tue: dayAvailable('tue'),
            available_wed: dayAvailable('wed'),
            available_thu: dayAvailable('thu'),
            available_fri: dayAvailable('fri'),
            available_sat: dayAvailable('sat'),
            available_sun: dayAvailable('sun'),
            time_morning: anyMorning,
            time_afternoon: anyAfternoon,

            // Interests
            open_roberts_house: form.open_roberts_house,
            open_depot_museum: form.open_depot_museum,
            host_school_tours: form.host_school_tours,

            clerical_skills: form.clerical_skills,
            clerical_skills_notes: form.clerical_skills_notes.trim(),

            grant_writing: form.grant_writing,

            maintenance_repairs: form.maintenance_repairs,
            maintenance_carpentry: form.maintenance_carpentry,
            maintenance_plumbing: form.maintenance_plumbing,
            maintenance_electrical: form.maintenance_electrical,
            maintenance_painting: form.maintenance_painting,
            maintenance_other: form.maintenance_other,
            maintenance_other_notes: form.maintenance_other_notes.trim(),

            fundraising_projects: form.fundraising_projects,
            public_relations: form.public_relations,
            pr_newspapers: form.pr_newspapers,
            pr_social_media: form.pr_social_media,
            pr_website: form.pr_website,

            research_historical_topics: form.research_historical_topics,
            holiday_decorating: form.holiday_decorating,
            correspondence: form.correspondence,
            yard_maintenance: form.yard_maintenance,

            computer_skills: form.computer_skills,
            computer_skills_notes: form.computer_skills_notes.trim(),

            host_museum_receptions: form.host_museum_receptions,

            housekeeping: form.housekeeping,
            housekeeping_roberts_home: form.housekeeping_roberts_home,
            housekeeping_depot_museum: form.housekeeping_depot_museum,

            providing_speakers: form.providing_speakers,
            safety_security: form.safety_security,

            other_talents: form.other_talents,
            other_talents_notes: form.other_talents_notes.trim(),
        };
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!formOk) {
            setError('Please complete all required sections before submitting.');
            return;
        }

        setError('');
        setSubmitting(true);
        try {
            const payload = buildPayload();
            const res = await apiFetch('/api/volunteers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.error || 'Submission failed. Please try again.');
            }
            onSuccess?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box component="form" id={formId} onSubmit={onSubmit}>
            <GlobalStyles styles={{ '.react-datepicker-popper': { zIndex: 2000 } }} />
            {/* Lock every control while submitting */}
            <Box component="fieldset" disabled={submitting} sx={{ p: 0, m: 0, border: 0 }}>
                <Stack spacing={3}>
                    {/* CONTACT (Required) */}
                    <Section
                        title="Contact Information"
                        subtitle="We’ll only use this to coordinate your volunteer availability."
                        required
                    >
                        {/* Use CSS Grid for precise placement */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' },
                                gap: 2,
                            }}
                        >
                            {/* First / Last — same line */}
                            <TextField
                                label="First Name"
                                value={form.first_name}
                                onChange={setSanitized('first_name', 50)}
                                required
                                fullWidth
                                inputProps={{ maxLength: 50 }}
                                sx={{ gridColumn: { xs: '1 / -1', md: 'span 6' } }}
                            />
                            <TextField
                                label="Last Name"
                                value={form.last_name}
                                onChange={setSanitized('last_name', 50)}
                                required
                                fullWidth
                                inputProps={{ maxLength: 50 }}
                                sx={{ gridColumn: { xs: '1 / -1', md: 'span 6' } }}
                            />

                            {/* Email (wide) + Phone (short) — same line */}
                            <TextField
                                label="Email"
                                type="email"
                                value={form.email_address}
                                onChange={setSanitized('email_address', 85)}
                                required
                                fullWidth
                                inputProps={{ maxLength: 85 }}
                                sx={{ gridColumn: { xs: '1 / -1', md: 'span 6' } }}
                            />
                            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 6' } }}>
                                <TextField
                                    label="Best Contact Phone"
                                    type="tel"
                                    value={form.phone_nbr}
                                    onChange={setPhone}
                                    required
                                    fullWidth
                                    inputProps={{ maxLength: 25, inputMode: 'tel' }}
                                />
                                <FormControl sx={{ mt: 1 }}>
                                    <RadioGroup
                                        row
                                        name="phone_type"
                                        value={form.phone_type}
                                        onChange={(e) => setForm((f) => ({ ...f, phone_type: e.target.value }))}
                                    >
                                        <FormControlLabel value="home" control={<Radio />} label="Home" />
                                        <FormControlLabel value="cell" control={<Radio />} label="Cell" />
                                        <FormControlLabel value="office" control={<Radio />} label="Office" />
                                    </RadioGroup>
                                </FormControl>
                            </Box>

                            {/* Address — full width, above City/State/Zip */}
                            <TextField
                                label="Address"
                                value={form.address}
                                onChange={setSanitized('address', 140)}
                                required
                                fullWidth
                                inputProps={{ maxLength: 140 }}
                                sx={{ gridColumn: '1 / -1' }}
                            />

                            {/* City / State / Zip */}
                            <TextField
                                label="City"
                                value={form.city}
                                onChange={setSanitized('city', 80)}
                                required
                                fullWidth
                                inputProps={{ maxLength: 80 }}
                                sx={{ gridColumn: { xs: '1 / -1', md: 'span 5' } }}
                            />
                            <TextField
                                select
                                label="State"
                                value={form.state}
                                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                                required
                                fullWidth
                                sx={{ gridColumn: { xs: '1 / -1', md: 'span 4' } }}
                            >
                                {US_STATES.map((st) => (
                                    <MenuItem key={st.value} value={st.value}>
                                        {st.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Zip"
                                value={form.zip_code}
                                onChange={setZip}
                                fullWidth
                                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                                sx={{ gridColumn: { xs: '1 / -1', md: 'span 3' } }}
                            />

                            {/* Occupation — full width */}
                            <TextField
                                label="Occupation"
                                value={form.occupation}
                                onChange={setSanitized('occupation', 120)}
                                fullWidth
                                inputProps={{ maxLength: 120 }}
                                sx={{ gridColumn: '1 / -1' }}
                            />

                            {/* Hobbies & Interests — full width, clean look */}
                            <TextField
                                label="Hobbies & Interests"
                                value={form.hobbies}
                                onChange={setSanitized('hobbies', 500)}
                                fullWidth
                                multiline
                                minRows={6}
                                inputProps={{ maxLength: 500 }}
                                sx={{ gridColumn: '1 / -1' }}
                            />
                        </Box>
                    </Section>

                    {/* AVAILABILITY (Required) */}
                    <Section
                        title="Availability"
                        subtitle="Check the times you can help on each day."
                        required
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr repeat(2, max-content)',
                                alignItems: 'center',
                                columnGap: 2,
                                rowGap: 0.5,
                                maxWidth: 520,
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 700, px: 1 }}>
                                Day
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, px: 1 }}>
                                Morning (9–12pm)
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, px: 1 }}>
                                Afternoon (12–3pm)
                            </Typography>

                            {DAYS.map((d) => (
                                <React.Fragment key={d.key}>
                                    <Typography variant="body2" sx={{ py: 0.5 }}>
                                        {d.label}
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={form[`${d.key}_morning`]}
                                                onChange={handleDay(d.key, 'morning')}
                                                inputProps={{ 'aria-label': `${d.label} morning` }}
                                            />
                                        }
                                        label=""
                                        sx={{ m: 0 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={form[`${d.key}_afternoon`]}
                                                onChange={handleDay(d.key, 'afternoon')}
                                                inputProps={{ 'aria-label': `${d.label} afternoon` }}
                                            />
                                        }
                                        label=""
                                        sx={{ m: 0 }}
                                    />
                                </React.Fragment>
                            ))}
                        </Box>
                        {!availabilityOk && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                Please select at least one time slot (morning or afternoon).
                            </Typography>
                        )}
                    </Section>

                    {/* INTERESTS & SKILLS (Required) */}
                    <Section
                        title="Interests & Skills"
                        subtitle="Select all the areas you’d like to help with. Add details where requested."
                        required
                    >
                        <Grid container spacing={2}>
                            {/* LEFT COLUMN */}
                            <Grid item xs={12} sm={6}>
                                {/* Tours & Visitor Services */}
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                                        Tours &amp; Visitor Services
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.open_roberts_house}
                                                    onChange={handleCheck('open_roberts_house')}
                                                />
                                            }
                                            label="Open the Roberts House for tours"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.open_depot_museum}
                                                    onChange={handleCheck('open_depot_museum')}
                                                />
                                            }
                                            label="Open the Depot Museum for tours"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.host_school_tours}
                                                    onChange={handleCheck('host_school_tours')}
                                                />
                                            }
                                            label="Host or Hostess for school tours"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.host_museum_receptions}
                                                    onChange={handleCheck('host_museum_receptions')}
                                                />
                                            }
                                            label="Host or Hostess at Museum Receptions"
                                        />
                                    </FormGroup>
                                </Box>

                                {/* Seasonal & Events */}
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                                        Seasonal &amp; Events
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.holiday_decorating}
                                                    onChange={handleCheck('holiday_decorating')}
                                                />
                                            }
                                            label="Holiday Decorating"
                                        />
                                    </FormGroup>
                                </Box>

                                {/* Public Relations & Communications */}
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                                        Public Relations &amp; Communications
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.public_relations}
                                                    onChange={handleToggleGroup('public_relations', [
                                                        'pr_newspapers',
                                                        'pr_social_media',
                                                        'pr_website',
                                                    ])}
                                                />
                                            }
                                            label="Public Relations"
                                        />
                                        <Box sx={{ pl: 3, opacity: form.public_relations ? 1 : 0.5 }}>
                                            <FormGroup row>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.pr_newspapers}
                                                            onChange={handleCheck('pr_newspapers')}
                                                            disabled={!form.public_relations}
                                                        />
                                                    }
                                                    label="Newspapers"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.pr_social_media}
                                                            onChange={handleCheck('pr_social_media')}
                                                            disabled={!form.public_relations}
                                                        />
                                                    }
                                                    label="Social Media"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.pr_website}
                                                            onChange={handleCheck('pr_website')}
                                                            disabled={!form.public_relations}
                                                        />
                                                    }
                                                    label="Website"
                                                />
                                            </FormGroup>
                                        </Box>

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.correspondence}
                                                    onChange={handleCheck('correspondence')}
                                                />
                                            }
                                            label="Writing/typing/texting correspondence"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.providing_speakers}
                                                    onChange={handleCheck('providing_speakers')}
                                                />
                                            }
                                            label="Providing speakers at Society meetings"
                                        />
                                    </FormGroup>
                                </Box>

                                {/* Fundraising & Grants */}
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                                        Fundraising &amp; Grants
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.fundraising_projects}
                                                    onChange={handleCheck('fundraising_projects')}
                                                />
                                            }
                                            label="Fundraising Projects"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.grant_writing}
                                                    onChange={handleCheck('grant_writing')}
                                                />
                                            }
                                            label="Grant Writing"
                                        />
                                    </FormGroup>
                                </Box>

                                {/* Other Talents */}
                                <Box sx={{ mb: 0 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                                        Other Talents
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.other_talents}
                                                    onChange={handleCheck('other_talents')}
                                                />
                                            }
                                            label="Other Talents (please list)"
                                        />
                                        <TextField
                                            value={form.other_talents_notes}
                                            onChange={setSanitized('other_talents_notes', 300)}
                                            placeholder="Tell us more about other talents"
                                            size="medium"
                                            fullWidth
                                            sx={{ mt: 1.5 }}
                                            disabled={!form.other_talents}
                                            inputProps={{ maxLength: 300, style: { fontSize: '1rem' } }}
                                        />
                                    </FormGroup>
                                </Box>
                            </Grid>

                            {/* RIGHT COLUMN */}
                            <Grid item xs={12} sm={6}>
                                {/* Facilities & Maintenance */}
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                                        Facilities &amp; Maintenance
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.maintenance_repairs}
                                                    onChange={handleToggleGroup('maintenance_repairs', [
                                                        'maintenance_carpentry',
                                                        'maintenance_plumbing',
                                                        'maintenance_electrical',
                                                        'maintenance_painting',
                                                        'maintenance_other',
                                                    ])}
                                                />
                                            }
                                            label="Maintenance / Repairs"
                                        />
                                        <Box sx={{ pl: 3, opacity: form.maintenance_repairs ? 1 : 0.5 }}>
                                            <FormGroup row>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.maintenance_carpentry}
                                                            onChange={handleCheck('maintenance_carpentry')}
                                                            disabled={!form.maintenance_repairs}
                                                        />
                                                    }
                                                    label="Carpentry"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.maintenance_plumbing}
                                                            onChange={handleCheck('maintenance_plumbing')}
                                                            disabled={!form.maintenance_repairs}
                                                        />
                                                    }
                                                    label="Plumbing"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.maintenance_electrical}
                                                            onChange={handleCheck('maintenance_electrical')}
                                                            disabled={!form.maintenance_repairs}
                                                        />
                                                    }
                                                    label="Electrical"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.maintenance_painting}
                                                            onChange={handleCheck('maintenance_painting')}
                                                            disabled={!form.maintenance_repairs}
                                                        />
                                                    }
                                                    label="Painting"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.maintenance_other}
                                                            onChange={handleCheck('maintenance_other')}
                                                            disabled={!form.maintenance_repairs}
                                                        />
                                                    }
                                                    label="Other"
                                                />
                                            </FormGroup>
                                            <TextField
                                                value={form.maintenance_other_notes}
                                                onChange={setSanitized('maintenance_other_notes', 300)}
                                                placeholder="Describe other maintenance skills"
                                                size="medium"
                                                fullWidth
                                                sx={{ mt: 2 }}
                                                disabled={!form.maintenance_repairs || !form.maintenance_other}
                                                inputProps={{ maxLength: 300, style: { fontSize: '1rem' } }}
                                            />
                                        </Box>

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.housekeeping}
                                                    onChange={handleToggleGroup('housekeeping', [
                                                        'housekeeping_roberts_home',
                                                        'housekeeping_depot_museum',
                                                    ])}
                                                />
                                            }
                                            label="Housekeeping"
                                        />
                                        <Box sx={{ pl: 3, opacity: form.housekeeping ? 1 : 0.5 }}>
                                            <FormGroup row>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.housekeeping_roberts_home}
                                                            onChange={handleCheck('housekeeping_roberts_home')}
                                                            disabled={!form.housekeeping}
                                                        />
                                                    }
                                                    label="Roberts Home"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={form.housekeeping_depot_museum}
                                                            onChange={handleCheck('housekeeping_depot_museum')}
                                                            disabled={!form.housekeeping}
                                                        />
                                                    }
                                                    label="Depot Museum"
                                                />
                                            </FormGroup>
                                        </Box>

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.yard_maintenance}
                                                    onChange={handleCheck('yard_maintenance')}
                                                />
                                            }
                                            label="Yard Maintenance"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.safety_security}
                                                    onChange={handleCheck('safety_security')}
                                                />
                                            }
                                            label="Safety & Security"
                                        />
                                    </FormGroup>
                                </Box>

                                {/* Research & Collections */}
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                                        Research &amp; Collections
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.research_historical_topics}
                                                    onChange={handleCheck('research_historical_topics')}
                                                />
                                            }
                                            label="Research Historical Topics"
                                        />
                                    </FormGroup>
                                </Box>

                                {/* Administrative & Technical */}
                                <Box sx={{ mb: 0 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                                        Administrative &amp; Technical
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.clerical_skills}
                                                    onChange={handleCheck('clerical_skills')}
                                                />
                                            }
                                            label="Clerical Skills (please list)"
                                        />
                                        <TextField
                                            value={form.clerical_skills_notes}
                                            onChange={setSanitized('clerical_skills_notes', 300)}
                                            placeholder="Typing, filing, phone, etc."
                                            size="medium"
                                            fullWidth
                                            sx={{ mt: 1.5 }}
                                            disabled={!form.clerical_skills}
                                            inputProps={{ maxLength: 300, style: { fontSize: '1rem' } }}
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.computer_skills}
                                                    onChange={handleCheck('computer_skills')}
                                                />
                                            }
                                            label="Computer Skills (please list)"
                                        />
                                        <TextField
                                            value={form.computer_skills_notes}
                                            onChange={setSanitized('computer_skills_notes', 300)}
                                            placeholder="e.g., MS Office, photo editing, web content"
                                            size="medium"
                                            fullWidth
                                            sx={{ mt: 1.5 }}
                                            disabled={!form.computer_skills}
                                            inputProps={{ maxLength: 300, style: { fontSize: '1rem' } }}
                                        />
                                    </FormGroup>
                                </Box>
                            </Grid>
                        </Grid>

                        {!interestsOk && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                Please select at least one interest or skill.
                            </Typography>
                        )}
                    </Section>

                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </Box>
        </Box>
    );
}

/* —————————————————————————————————————
   Page wrapper – CTA + popup with footer Submit
   ————————————————————————————————————— */
export default function Volunteers() {
    const [open, setOpen] = useState(false);
    const [justSubmitted, setJustSubmitted] = useState(false);

    // footer controls from child
    const [canSubmit, setCanSubmit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleOpen = () => {
        setJustSubmitted(false);
        setOpen(true);
    };
    const handleClose = () => setOpen(false);

    const FORM_ID = 'volunteer-form';

    return (
        <>
            <Container maxWidth="lg" sx={{ py: { xs: 5, md: 6 }, px: { xs: 2, md: 0 } }}>
                {/* Intro */}
                <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ fontWeight: 700, fontSize: { xs: '1.35rem', sm: '1.5rem' }, lineHeight: { xs: 1.3 } }}
                    >
                        Become a Volunteer
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'left',
                            maxWidth: 800,
                            mx: 'auto',
                            fontSize: { xs: '1rem' },
                            lineHeight: { xs: 1.7, md: 1.75 },
                        }}
                    >
                        Volunteering your time, talents, and skills is a vital part of the success of the Piedmont Historical Society in carrying out its educational mission to "Collect, preserve, and communicate the history of Piedmont and Alabama to all people." Thank you!
                    </Typography>
                </Box>

                {/* Image + CTA */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 3, sm: 4, md: 6 },
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        px: { xs: 0, md: 0 },
                    }}
                >
                    <Box
                        component="img"
                        src={volunteerImg}
                        alt="Volunteer at the Museum"
                        sx={{
                            width: { xs: '100%', md: '45%' },
                            height: 'auto',
                            borderRadius: { xs: 2, md: 2 },
                            boxShadow: { xs: 1, md: 0 },
                        }}
                    />

                    <Box sx={{ width: { xs: '100%', md: '55%' } }}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: { xs: 2.5, md: 3 },
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: { xs: 1.5, md: 2 },
                            }}
                        >
                            {/* --- Perks --- */}
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem' } }}>
                                Volunteer Perks
                            </Typography>
                            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Volunteers receive <strong>free membership benefits</strong>:
                                </Typography>
                                <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 28 }}>
                                    <li>Educational lectures every two months (6 per year)</li>
                                    <li>Access to activities at the Depot</li>
                                    <li>Access to Robert's Home museum when not in use</li>
                                    <li>Free admission during regular Depot hours</li>
                                </ul>
                            </Alert>

                            <Divider sx={{ my: 1 }} />

                            {/* CTA */}
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.05rem' } }}>
                                Ready to help? We’d love to meet you.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Click below to sign up and tell us your interests and availability.
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Button variant="contained" size="large" onClick={handleOpen} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                    Sign Up to Volunteer
                                </Button>
                            </Box>

                            {justSubmitted && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    Thank you! Your application was received—we’ll be in touch soon.
                                </Alert>
                            )}
                        </Paper>
                    </Box>
                </Box>
            </Container>

            {/* Popup Dialog with the full form */}
            <Dialog
                open={open}
                // Prevent closing while submitting (locks dialog during submit)
                onClose={(_, reason) => {
                    if (isSubmitting && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
                    handleClose();
                }}
                disableEscapeKeyDown={isSubmitting}
                fullScreen={fullScreen}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{ sx: { borderRadius: { xs: 0, md: 2 } } }}
            >
                <DialogTitle sx={{ pr: 6, py: { xs: 1.25, md: 2 } }}>
                    Sign Up to Volunteer
                    <IconButton aria-label="close" onClick={handleClose} disabled={isSubmitting} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ bgcolor: 'background.default', px: { xs: 2, md: 3 } }}>
                    <VolunteerForm
                        formId={FORM_ID}
                        onSuccess={() => {
                            setJustSubmitted(true);
                            handleClose();
                        }}
                        onValidityChange={setCanSubmit}
                        onSubmittingChange={setIsSubmitting}
                    />
                </DialogContent>

                {/* Actions: stack on phones, keep row on desktop */}
                <DialogActions
                    sx={{
                        p: 2,
                        flexWrap: 'wrap',
                        gap: 1,
                        '> .MuiTypography-root': { flex: 1 },
                    }}
                >
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                        Your information is only used to coordinate volunteer opportunities.
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Button onClick={handleClose} disabled={isSubmitting} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            form={FORM_ID}
                            variant="contained"
                            disabled={!canSubmit || isSubmitting}
                            sx={{
                                width: { xs: '100%', sm: 'auto' },
                                '&.Mui-disabled': { color: 'common.white', bgcolor: 'primary.main', opacity: 0.75 },
                            }}
                        >
                            {isSubmitting ? 'Submitting…' : 'Submit Application'}
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>
        </>
    );
}
